import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Expander from '@amzn/meridian/expander';
import Row from '@amzn/meridian/row';
import styled from '@emotion/styled';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { Trans } from 'react-i18next';
import { BorderedColumn, GreyBox, Heading, SearchField, Text, useTranslation } from '../components/blocks';
import { PaginatedTable } from '../components/constructed';
import { NEWS_ARTICLES, newsFeedImages } from '../constants';
import { useReturnTrackingFeature } from '../hooks/useReturnTrackingFeature';
import { TableData, NewsBlock } from '../redux/types';
import { Logger } from '../utils/logger';

const NewsImage = styled.img`
    width: 100%;
    height: 100%;
    background-color: white;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
`;

const StyledColumn = styled(Column)`
    div[role='button']:focus {
        outline: thin dotted;
    }
`;

// eslint-disable-next-line max-lines-per-function
export const NewsPage: any = () => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [sortColumn, setSortColumn] = useState<string>('date');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('descending');
    const [curSearchValue, setCurSearchValue] = useState<string>('');

    const ARTICLES_PER_PAGE: number = 10;

    const { t } = useTranslation('newsPage');

    const newsBlocks = new Map();
    const { isReturnTrackingEnabled } = useReturnTrackingFeature();

    // Populates news blocks
    NEWS_ARTICLES.filter((articleFilter) => {
        const isInDateRange = articleFilter.startDate < new Date().getTime();
        const passesFeatureFlag = !articleFilter.requiresReturnTracking || isReturnTrackingEnabled;
        return isInDateRange && passesFeatureFlag;
    }).map((article) => {
        const section: NewsBlock = {
            title: article.articleTitleId,
            date: new Date(article.articleDate),
            content: {
                videoUrl: article.videoUrls,
                picturesByRow: article.picturesByRow,
                text: article.articleTextIds,
            },
        };
        newsBlocks.set(section.title, section);
        return section;
    });

    const ControlledExpander = (props: any) => {
        const [open, setOpen] = useState();
        return <Expander open={open} onChange={setOpen} {...props} />;
    };

    function foundNews(element: NewsBlock) {
        const lowerCurSearchValue = curSearchValue.toLowerCase();

        const found = curSearchValue && t(element.title)?.toLowerCase().includes(lowerCurSearchValue);

        return found;
    }

    const formatSortColumn = (newsBlock: NewsBlock) => {
        switch (sortColumn) {
            case 'title':
                return `${newsBlock.title}`;
            case 'date':
                return `${newsBlock.date}`;
            default:
                return `${newsBlock.title}`;
        }
    };

    const getNewsBlocks = (blocks: NewsBlock[]) => {
        const compare = function (articleA: NewsBlock, articleB: NewsBlock) {
            const direction = sortDirection === 'descending' ? -1 : 1;
            if (sortColumn === 'date') {
                return direction * (articleA.date.getTime() - articleB.date.getTime());
            }
            return direction * formatSortColumn(articleA).localeCompare(formatSortColumn(articleB));
        };

        const sortedArticles = blocks.sort(compare);

        const startPos = (currentPage - 1) * ARTICLES_PER_PAGE;
        const endPos = currentPage * ARTICLES_PER_PAGE;

        const pageArticles: NewsBlock[] = sortedArticles.slice(startPos, endPos);

        const tableData = new TableData(pageArticles, [
            {
                title: t('titleHeader'),
                uniqueId: 'title',
                format: (block: NewsBlock) => {
                    return (
                        <StyledColumn>
                            <ControlledExpander title={`${t(block.title)}`} type={'section'} key={`${block.title}-key`}>
                                <Column width={'fill'} maxWidth={'65em'} spacing={'400'}>
                                    <Column width={'fill'} maxWidth={'65em'} spacing={'200'}>
                                        {block.content.text.map((newsText) => (
                                            <React.Fragment key={newsText.textId}>
                                                <Text type={'b200'} alignment={'left'}>
                                                    <Trans
                                                        i18nKey={newsText.textId}
                                                        ns={'newsPage'}
                                                        components={{
                                                            addTracking: (
                                                                <a
                                                                    href={'/help/view/help#add-tracking'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            addReturnReason: (
                                                                <a
                                                                    href={'/help/view/help#add-return-reason'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            manageUsers: (
                                                                <a
                                                                    href={'/help/view/help#manage-users'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            createUser: (
                                                                <a
                                                                    href={'/help/view/help#create-user'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            uploadReturn: (
                                                                <a
                                                                    href={'/help/view/help#upload-return'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            createReturn: (
                                                                <a
                                                                    href={'/help/view/help#create-return'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            creditMemo: (
                                                                <a
                                                                    href={'/help/view/help#credit-memo'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            uploadContractualClaim: (
                                                                <a
                                                                    href={'/help/view/help#upload-contractual-claim'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            uploadPromotionalClaim: (
                                                                <a
                                                                    href={'/help/view/help#upload-promotional-claim'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            viewClaims: (
                                                                <a
                                                                    href={'/help/view/help#view-claims'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            viewPurchaseOrders: (
                                                                <a
                                                                    href={'/help/view/help#view-purchase-orders'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                            purchaseOrderReports: (
                                                                <a
                                                                    href={'/help/view/help#purchase-order-reports'}
                                                                    target={'_blank'}
                                                                    rel={'noopener noreferrer'}
                                                                >
                                                                    {' '}
                                                                </a>
                                                            ),
                                                        }}
                                                    />
                                                </Text>
                                                {newsText.subtextIds?.map((subTextId) => (
                                                    <Row widths={['2em', 'fill']} spacing={'none'} key={subTextId}>
                                                        <Box />
                                                        <Text type={'b200'} alignment={'left'}>
                                                            {t(subTextId)}
                                                        </Text>
                                                    </Row>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </Column>
                                    {block.content.videoUrl &&
                                        block.content.videoUrl.map((videoUrl) => {
                                            return (
                                                <iframe
                                                    width={'560'}
                                                    height={'315'}
                                                    src={videoUrl}
                                                    title={'YouTube video player'}
                                                    frameBorder={'0'}
                                                    allow={
                                                        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                                    }
                                                    allowFullScreen={true}
                                                    key={`${videoUrl}_${block.title}`}
                                                />
                                            );
                                        })}
                                    {block.content.picturesByRow &&
                                        block.content.picturesByRow.map((pictureRow) => {
                                            return (
                                                <Row
                                                    spacing={'xsmall'}
                                                    maxWidth={'65em'}
                                                    widths={[..._.fill(Array(pictureRow.length), 'fit')]}
                                                    key={pictureRow.toString()}
                                                >
                                                    {pictureRow.map((picture) => {
                                                        return (
                                                            <Column
                                                                maxWidth={`${(1 / pictureRow.length) * 100}%`}
                                                                key={picture}
                                                            >
                                                                <NewsImage
                                                                    src={
                                                                        newsFeedImages[
                                                                            picture as keyof typeof newsFeedImages
                                                                        ]
                                                                    }
                                                                    key={picture}
                                                                />
                                                            </Column>
                                                        );
                                                    })}
                                                </Row>
                                            );
                                        })}
                                </Column>
                            </ControlledExpander>
                        </StyledColumn>
                    );
                },
            },
            {
                title: t('dateHeader'),
                uniqueId: 'date',
                format: (block: NewsBlock) => (
                    <Text key={`${block.title} - ${block.date} - articleDate`}>
                        {t('dateFormat', { dateTime: block.date })}
                    </Text>
                ),
            },
        ]);
        return tableData;
    };

    const onPageClick = (value: string) => {
        setCurrentPage(parseInt(value));
    };

    const onSort = useCallback(({ sortColumn, sortDirection }) => {
        setSortColumn(sortColumn);
        setSortDirection(sortDirection);
    }, []);

    const onSearch = (value: string) => {
        Logger.debug(`onSearch: ${JSON.stringify({ value })}`);
        setCurSearchValue(value);
        setCurrentPage(1);
    };

    const newsBlocksList: NewsBlock[] = Array.from(newsBlocks.values());
    const filteredNewsBlocks: NewsBlock[] = curSearchValue ? newsBlocksList.filter(foundNews) : newsBlocksList;
    const remainder = filteredNewsBlocks.length % ARTICLES_PER_PAGE;
    const numberOfPages = (filteredNewsBlocks.length - remainder) / ARTICLES_PER_PAGE + (remainder > 0 ? 1 : 0);
    const data = getNewsBlocks(filteredNewsBlocks);

    return (
        <BorderedColumn alignmentHorizontal={'center'} alignmentVertical={'top'} minWidth={'80em'} spacing={'none'}>
            <GreyBox type={'fill'} spacingInset={'small'} width={'100%'}>
                <Heading level={5} alignment={'left'} data-testid={'NewsHeader'}>
                    {t('news-tableTitle', { count: newsBlocks.size })}
                </Heading>
            </GreyBox>
            <PaginatedTable
                currentPage={currentPage}
                data={data}
                numberOfPages={numberOfPages}
                onPageChange={onPageClick}
                onSort={onSort}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                rowKey={(rowArray: string[], rowObject: NewsBlock) => {
                    return rowObject.title.toString();
                }}
            >
                <Box width={'80%'} spacingInset={'none'} data-testid={'NewsListSearchBox'}>
                    <Row spacing={'small'}>
                        <SearchField
                            value={curSearchValue}
                            onChange={onSearch}
                            label={t('searchBar-placeholder')}
                            onSubmit={onSearch}
                            size={'medium'}
                            searchButton={false}
                            data-testid={'newsListPage.searchField'}
                        />
                    </Row>
                </Box>
            </PaginatedTable>
        </BorderedColumn>
    );
};
